/**
 * Lazy hydration for history-loaded workspace artifacts.
 */
import { getTenantIdFromStorage } from '../../../utils/onboardingLocalStorage';
import {
  fetchThreadArtifact,
  hydratedPayloadToArtifact,
} from '../../../services/chatHistoryService';
import { useCopilotStore, type WorkspaceArtifact } from '../stores/useCopilotStore';

export function getArtifactHydrationState(
  artifact: WorkspaceArtifact | null | undefined,
): 'ready' | 'stub' | 'loading' | 'error' {
  if (!artifact) return 'ready';
  if (artifact.hydration === 'loading') return 'loading';
  if (artifact.hydration === 'error') return 'error';
  if (artifact.hydration === 'ready') return 'ready';
  if (artifact.hydration === 'stub') return 'stub';
  if (artifact.eventId) {
    if (artifact.type === 'chart' && Object.keys(artifact.spec || {}).length > 0) return 'ready';
    if (artifact.type === 'query_result' && (artifact.rows?.length ?? 0) > 0) return 'ready';
    if (artifact.type === 'metric_card' && artifact.metricValue != null && artifact.metricValue !== '') {
      return 'ready';
    }
    return 'stub';
  }
  return 'ready';
}

export async function hydrateArtifactByRef(
  artifactRef: string,
  storeApi?: Pick<
    ReturnType<typeof useCopilotStore.getState>,
    'threadId' | 'workspaceTabs' | 'patchWorkspaceArtifact'
  >,
): Promise<void> {
  const store = storeApi ?? useCopilotStore.getState();
  const tab = store.workspaceTabs.find((t) => t.id === artifactRef);
  if (!tab) return;

  const state = getArtifactHydrationState(tab);
  if (state === 'ready' || state === 'loading') return;

  const threadId = store.threadId;
  const tenantId = getTenantIdFromStorage();
  const eventId = tab.eventId;
  if (!threadId || !tenantId || !eventId) return;

  store.patchWorkspaceArtifact(artifactRef, { hydration: 'loading', hydrationError: undefined });

  try {
    const payload = await fetchThreadArtifact(tenantId, threadId, eventId);
    const hydrated = hydratedPayloadToArtifact(payload);
    store.patchWorkspaceArtifact(artifactRef, { ...hydrated, hydration: 'ready' });
  } catch (err) {
    store.patchWorkspaceArtifact(artifactRef, {
      hydration: 'error',
      hydrationError: err instanceof Error ? err.message : 'Failed to load',
    });
  }
}

export async function hydrateAuditById(
  eventId: string,
  storeApi?: Pick<
    ReturnType<typeof useCopilotStore.getState>,
    'threadId' | 'auditReports' | 'addAuditReport'
  >,
): Promise<void> {
  const store = storeApi ?? useCopilotStore.getState();
  if (store.auditReports[eventId]) return;

  const threadId = store.threadId;
  const tenantId = getTenantIdFromStorage();
  if (!threadId || !tenantId) return;

  const { fetchThreadAudit } = await import('../../../services/chatHistoryService');
  const report = await fetchThreadAudit(tenantId, threadId, eventId);
  store.addAuditReport(report, eventId);
}
